import { envConfig } from './env'

/**
 * Gainsight PX (https://www.gainsight.com/product-experience/) is a product
 * analytics platform. The integration is intentionally limited to the
 * production deploy so we do not pollute the production dashboard with
 * traffic from local dev / staging / sandbox environments.
 *
 * The tag key is sourced from `envConfig.GAINSIGHT_PX_TAG_KEY` (managed by
 * SRE per environment, see `docs/ENVIRONMENT_VARIABLES.md`).
 *
 * SYN-6649: FE: Add support for Gainsight PX overlay
 */

const GAINSIGHT_PX_SDK_URL = 'https://web-sdk.aptrinsic.com/api/aptrinsic.js'

/**
 * Shape of the global `aptrinsic` queue/stub installed by the official
 * Gainsight PX snippet. Calls made before the SDK script has finished
 * loading are buffered on `q` and flushed by the SDK at startup.
 */
type AptrinsicQueueFn = {
  (...args: unknown[]): void
  q?: unknown[][]
  p?: string
  c?: unknown
}

declare global {
  interface Window {
    aptrinsic?: AptrinsicQueueFn
  }
}

export type GainsightPxUser = {
  id: string
  role?: string | null
  createdAt?: string | null
}

export type GainsightPxAccount = {
  id: string | number
  name?: string | null
  licenseType?: string | null
}

const isGainsightPxEnabled = (): boolean => envConfig.GAINSIGHT_PX_TAG_KEY !== ''

let isScriptInjected = false

/**
 * Installs the Gainsight PX tag on the page. Safe to call multiple times — the
 * script is only injected once. No-op outside the production environment or
 * when no tag key has been configured for the current env.
 */
export const loadGainsightPx = (): void => {
  if (!isGainsightPxEnabled()) return
  if (isScriptInjected) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const tagKey = envConfig.GAINSIGHT_PX_TAG_KEY

  // The official Gainsight PX snippet, translated to TypeScript. Sets up a
  // queueing stub at `window.aptrinsic` so any identify/track calls made
  // before the SDK script finishes loading are buffered and replayed once
  // the real SDK is ready.
  const queueFn: AptrinsicQueueFn = function (...args: unknown[]) {
    queueFn.q = queueFn.q || []
    queueFn.q.push(args)
  }
  queueFn.p = tagKey
  window.aptrinsic = queueFn

  const script = document.createElement('script')
  script.async = true
  script.src = `${GAINSIGHT_PX_SDK_URL}?a=${tagKey}`

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  isScriptInjected = true
}

/**
 * Associates the current browser session with a known user + account so that
 * subsequent events tracked by the Gainsight PX tag are attributed to them.
 * No-op outside production or before {@link loadGainsightPx} has run.
 *
 * The field names sent over the wire match the Gainsight PX out-of-the-box
 * user / account model:
 * https://support.gainsight.com/PX/Administration/General/User_and_Account_Model
 */
export const identifyGainsightPx = (user: GainsightPxUser, account: GainsightPxAccount): void => {
  if (!isGainsightPxEnabled()) return
  if (typeof window === 'undefined' || typeof window.aptrinsic !== 'function') return

  window.aptrinsic(
    'identify',
    {
      id: user.id,
      role: user.role ?? undefined,
      created_at: user.createdAt ?? undefined,
    },
    {
      id: String(account.id),
      name: account.name ?? undefined,
      license_type: account.licenseType ?? undefined,
    },
  )
}
